import {
  AI_PROFILES,
  type AiCapabilities,
  type AiProfile,
  type AiProviderId,
  type AiReasoningEffort,
} from '@fos/shared/contracts/ai';
import type { AiSettingsView, UpdateAiSettingsInput } from '@fos/shared/contracts/copilot';
import { AI_MODEL_ID_RE } from '@fos/shared/contracts/copilot';
import { AI_CAPABILITY_LABELS } from '@fos/shared/labels';

/**
 * Formularz „Model i limity” (Ustawienia → AI i pamięć): stan pól jako tekst (puste = bez limitu), walidacja w przeglądarce
 * (te same reguły co serwer; serwer i tak sprawdza) i mapowanie błędów serwera na pola.
 */

export interface PriceRow {
  key: string;
  model: string;
  inputPer1M: string;
  /** cena wejścia z cache (Fala 9); puste = jak wejście */
  cachedInputPer1M: string;
  outputPer1M: string;
}

/** Poziom rozumowania w formularzu: `default` = domyślny poziom modelu (parametr nie jest wysyłany). */
export type ReasoningChoice = AiReasoningEffort | 'default';

export interface AiSettingsForm {
  enabled: boolean;
  classifyIncoming: boolean;
  provider: AiProviderId;
  models: Record<AiProfile, string>;
  reasoning: Record<AiProfile, ReasoningChoice>;
  monthlyBudgetPln: string;
  perUserBudgetPln: string;
  maxToolIterations: string;
  timeoutSec: string;
  prices: PriceRow[];
}

export type FormErrors = Partial<Record<string, string>>;

let rowSeq = 0;
export function priceRow(model = '', inputPer1M = '', outputPer1M = '', cachedInputPer1M = ''): PriceRow {
  rowSeq += 1;
  return { key: `r${rowSeq}`, model, inputPer1M, cachedInputPer1M, outputPer1M };
}

export function formFromView(view: AiSettingsView): AiSettingsForm {
  const f = view.fields;
  return {
    enabled: f.enabled,
    classifyIncoming: f.classifyIncoming,
    provider: f.provider,
    models: {
      fast: f.profiles.fast?.model ?? '',
      smart: f.profiles.smart?.model ?? '',
      exec: f.profiles.exec?.model ?? '',
    },
    reasoning: {
      fast: f.profiles.fast?.reasoningEffort ?? 'default',
      smart: f.profiles.smart?.reasoningEffort ?? 'default',
      exec: f.profiles.exec?.reasoningEffort ?? 'default',
    },
    monthlyBudgetPln: f.monthlyBudgetPln ?? '',
    perUserBudgetPln: f.perUserBudgetPln ?? '',
    maxToolIterations: String(f.maxToolIterations),
    timeoutSec: String(Math.round(f.timeoutMs / 1000)),
    prices: Object.entries(f.priceTable).map(([model, p]) =>
      priceRow(model, p.inputPer1M, p.outputPer1M, p.cachedInputPer1M ?? ''),
    ),
  };
}

/**
 * Porównanie formularza z zapisanym stanem („Zapisz” aktywne tylko po zmianie): bez kluczy wierszy cennika — każde
 * `formFromView` nadaje nowe klucze, więc formularz z cennikiem był zawsze „zmieniony” (Fala 9: preset ma cennik).
 */
export function formSignature(form: AiSettingsForm): string {
  return JSON.stringify({ ...form, prices: form.prices.map(({ key: _key, ...row }) => row) });
}

const MONEY_RE = /^\d{1,10}([.,]\d{1,2})?$/;
const PRICE_RE = /^\d{1,6}([.,]\d{1,4})?$/;

function money(v: string): string | null {
  const t = v.trim().replace(/\s/g, '');
  return t ? t.replace(',', '.') : null;
}

/**
 * Walidacja + wejście `ai.updateSettings`; błędy pod kluczami pól formularza. `reasoning: false` = dostawca bez
 * poziomu rozumowania (pole wyłączone, nic nie jest wysyłane).
 */
export function toInput(
  form: AiSettingsForm,
  version: number | null,
  opts: { showMoney: boolean; reasoning?: boolean },
): { input: UpdateAiSettingsInput | null; errors: FormErrors } {
  const errors: FormErrors = {};
  const profiles: UpdateAiSettingsInput['profiles'] = {};
  for (const p of AI_PROFILES) {
    const model = form.models[p].trim();
    if (!model) continue;
    const effort = opts.reasoning === false ? 'default' : form.reasoning[p];
    if (!AI_MODEL_ID_RE.test(model)) errors[`profiles.${p}.model`] = 'Nieprawidłowy identyfikator modelu';
    else profiles[p] = { model, ...(effort !== 'default' ? { reasoningEffort: effort } : {}) };
  }
  if (form.provider !== 'fake') {
    for (const p of ['fast', 'smart'] as const) {
      if (!form.models[p].trim()) errors[`profiles.${p}.model`] = 'Podaj model dla tego profilu';
    }
  }
  const budgets = { monthlyBudgetPln: form.monthlyBudgetPln, perUserBudgetPln: form.perUserBudgetPln };
  for (const [k, v] of Object.entries(budgets)) {
    const t = v.trim().replace(/\s/g, '');
    if (t && !MONEY_RE.test(t)) errors[k] = 'Kwota w złotych, np. 200 albo 49,50';
  }
  const iterations = Number(form.maxToolIterations);
  if (!Number.isInteger(iterations) || iterations < 1 || iterations > 20) {
    errors.maxToolIterations = 'Od 1 do 20';
  }
  const timeout = Number(form.timeoutSec);
  if (!Number.isInteger(timeout) || timeout < 5 || timeout > 600) errors.timeoutMs = 'Od 5 do 600 sekund';

  const priceTable: UpdateAiSettingsInput['priceTable'] = {};
  form.prices.forEach((row, i) => {
    const model = row.model.trim();
    const cached = row.cachedInputPer1M.trim();
    if (!model && !row.inputPer1M.trim() && !cached && !row.outputPer1M.trim()) return;
    if (!AI_MODEL_ID_RE.test(model)) errors[`prices.${i}.model`] = 'Identyfikator modelu';
    else if (priceTable[model]) errors[`prices.${i}.model`] = 'Ten model już jest w cenniku';
    const input = row.inputPer1M.trim();
    const output = row.outputPer1M.trim();
    if (!PRICE_RE.test(input)) errors[`prices.${i}.inputPer1M`] = 'Cena, np. 12,50';
    if (cached && !PRICE_RE.test(cached)) errors[`prices.${i}.cachedInputPer1M`] = 'Cena, np. 0,04';
    if (!PRICE_RE.test(output)) errors[`prices.${i}.outputPer1M`] = 'Cena, np. 60';
    if (model)
      priceTable[model] = {
        inputPer1M: input.replace(',', '.'),
        ...(cached ? { cachedInputPer1M: cached.replace(',', '.') } : {}),
        outputPer1M: output.replace(',', '.'),
      };
  });
  if (Object.keys(priceTable).length > 30) errors.priceTable = 'Najwyżej 30 modeli w cenniku';

  if (Object.keys(errors).length > 0) return { input: null, errors };
  return {
    errors,
    input: {
      version,
      enabled: form.enabled,
      provider: form.provider,
      profiles,
      monthlyBudgetPln: opts.showMoney ? money(form.monthlyBudgetPln) : null,
      perUserBudgetPln: opts.showMoney ? money(form.perUserBudgetPln) : null,
      maxToolIterations: iterations,
      timeoutMs: timeout * 1000,
      priceTable: opts.showMoney ? priceTable : {},
      classifyIncoming: form.classifyIncoming,
    },
  };
}

/** Ścieżka błędu serwera → klucz pola (np. `priceTable` → ogólny błąd cennika). */
export function errorsFromIssues(issues: { path: string; message: string }[]): FormErrors {
  const out: FormErrors = {};
  for (const i of issues) {
    const key = i.path.startsWith('priceTable') ? 'priceTable' : i.path || 'form';
    if (!out[key]) out[key] = i.message;
  }
  return out;
}

/** „narzędzia · odpowiedź w formacie JSON” — obsługiwane możliwości adaptera. */
export function capabilitiesText(caps: AiCapabilities | null): string {
  if (!caps) return 'możliwości nieznane';
  const on = (Object.keys(AI_CAPABILITY_LABELS) as (keyof AiCapabilities)[]).filter((k) => caps[k]);
  return on.length ? on.map((k) => AI_CAPABILITY_LABELS[k]).join(' · ') : 'brak';
}

/** Miesiące do wyboru zużycia: bieżący i 5 poprzednich ("2026-10"). */
export function recentMonths(today: string, count = 6): string[] {
  const [y, m] = today.split('-').map(Number) as [number, number];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

const MONTHS = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
] as const;

/** "2026-10" → "październik 2026" */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${y}`;
}
